import { z } from 'zod';
import { ref, unref } from 'vue';
import type { MaybeRef } from 'vue';
import { isNil, startCase } from 'lodash-es';
import { isAxiosError } from 'axios';
import {
  AwsCollectorCreateCreateUpdateRequestService,
  AzureCollectorCreateCreateUpdateRequestService,
  CloudCommonServiceItem,
} from '@/api-client/cloud/model';
import { getV8CloudCommonServiceByResourceId } from '@/api-client/cloud/queries/cloud-common-controller/cloud-common-controller';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import {
  CLOUD,
  CLOUD_ALL_SERVICE_LABEL,
} from '@/config/views/platform/cloud/define/cloudService.define';
import { getCloudServiceType } from '@/cloud/utils/utils';
import { AWS_SERVICE, EXCLUDE_DETAIL_CATEGORY } from '@/cloud/utils/define';
import {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';

const ALLOWED_CLOUD: Set<string> = new Set([CLOUD.AWS, CLOUD.AZURE]);

const ALLOWED_CLOUD_SERVICES: Set<string> = new Set([
  ...Object.values(AwsCollectorCreateCreateUpdateRequestService),
  ...Object.values(AzureCollectorCreateCreateUpdateRequestService),
]);

const checkIsPermissionError = (error: unknown) => {
  return (
    isAxiosError(error) &&
    error.response?.status === 406 &&
    error.response?.data?.errorType === 'PermissionUnauthorizedException'
  );
};

// targetId 필수, 없으면 getDetailParams에서 채움
const cloudDetailInputObject = z.object({
  targetId: z.string().min(1),
  cloud: z.string().nullish(),
  service: z.string().nullish(),
  targetName: z.string().nullish(),
  targetAlias: z.string().nullish(),
  instanceType: z.string().nullish(),
  clusterMode: z.string().nullish(),
  fromTime: z.string().nullish(),
  toTime: z.string().nullish(),
  rawData: z.record(z.any()).optional(),
});

const cloudDetailInputSchema = cloudDetailInputObject
  .transform(
    ({
      targetId,
      cloud,
      service,
      targetName,
      targetAlias,
      instanceType,
      clusterMode,
      fromTime,
      toTime,
      rawData,
    }) => ({
      resourceId: targetId,
      cloud: (cloud || rawData?.cloud)?.toLowerCase() ?? '',
      service: service || rawData?.service || '',
      targetName: targetName || rawData?.targetName,
      targetAlias: targetAlias || rawData?.targetAlias,
      subType: instanceType || rawData?.instanceType,
      clusterMode: clusterMode || rawData?.clusterMode,
      fromTime: fromTime || rawData?.fromTime,
      toTime: toTime || rawData?.toTime,
    }),
  )
  .pipe(
    z
      .object({
        resourceId: z.string().min(1),
        cloud: z.string().min(1),
        service: z.string().min(1),
        targetName: z.string().min(1),
        targetAlias: z.string().nullish(),
        subType: z.string().nullish(),
        clusterMode: z.string().nullish(),
        fromTime: z.string().nullish(),
        toTime: z.string().nullish(),
      })
      .superRefine((data, ctx) => {
        if (!ALLOWED_CLOUD.has(data.cloud)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cloud'],
          });
        }
        if (!ALLOWED_CLOUD_SERVICES.has(data.service)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['service'],
          });
        }
        if (data.service.includes('ELASTICACHE_CLUSTER')) {
          const valid = data.clusterMode != null && String(data.clusterMode).trim() !== '';
          if (!valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['clusterMode'],
            });
          }
        }
        if (data.service.includes('DOCUMENTDB')) {
          const valid = data.subType != null && String(data.subType).trim() !== '';
          if (!valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['instanceType'],
            });
          }
        }
      }),
  );

export type CloudDetailInput = z.input<typeof cloudDetailInputSchema>;

export const cloudDetailRequiredMap = getRequiredMap(cloudDetailInputSchema);

export const useCloudDetailAdapter = (rolePermissionKey?: MaybeRef<RolePermissionKeyType>) => {
  const { t } = useInternational();
  const isSlideDetailOpened = ref(false);
  const { addTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();
  const { showErrorMsg } = useMessage();

  const fetchCloudService = async (targetId: string): Promise<CloudCommonServiceItem> => {
    if (!targetId) return {};
    try {
      const res = await getV8CloudCommonServiceByResourceId(targetId);
      const { cloud, service, name, alias, instanceType, clusterMode } = res?.data?.data?.[0] ?? {};
      return { cloud, service, name, alias, instanceType, clusterMode };
    } catch (error) {
      if (checkIsPermissionError(error)) {
        throw error;
      }
      console.error(error);
      return {};
    }
  };

  const getInstanceType = (service: string, instanceType?: string | null) => {
    if (service === AWS_SERVICE.DOCUMENTDB) {
      if (!instanceType) return instanceType;
      return instanceType?.toLowerCase()?.includes('cluster') ? 'cluster' : 'instance';
    }
    return instanceType;
  };

  const getDetailParams = async (params: CloudDetailInput): Promise<CloudDetailInput> => {
    const hasRequiredParams = Boolean(
      params.cloud && params.service && params.targetName && params.targetAlias,
    );

    let cloud = params.cloud ?? '';
    let service = params.service ?? '';
    let targetName = params.targetName ?? '';
    let targetAlias = params.targetAlias ?? '';
    let instanceTypeRaw = params.instanceType;
    let clusterMode = params.clusterMode;

    if (!hasRequiredParams) {
      const fetched = await fetchCloudService(params.targetId);
      cloud = fetched.cloud ?? params.cloud ?? '';
      service = fetched.service ?? params.service ?? '';
      targetName = fetched.name ?? params.targetName ?? '';
      targetAlias = fetched.alias ?? params.targetAlias ?? '';
      instanceTypeRaw = fetched.instanceType ?? params.instanceType;
      clusterMode = fetched.clusterMode ?? params.clusterMode;
    }

    return {
      ...params,
      cloud,
      service,
      targetName,
      targetAlias,
      instanceType: getInstanceType(service, instanceTypeRaw),
      clusterMode,
    };
  };

  const getDetailKeyFromPermissionKey = (fullKey: string, cloud: string, service: string) => {
    const monitoringKeys = ['computing', 'storage', 'database'];
    const isMonitoringKey =
      monitoringKeys.some((key) => fullKey.includes(key)) && !fullKey.includes('aws');

    if (isMonitoringKey) {
      return TOTAL_DETAIL[fullKey.split('monitoring_')[1]?.trim()?.toUpperCase()];
    }
    return TOTAL_DETAIL[`${cloud.toUpperCase()}_${service.toUpperCase()}`];
  };

  const getDetailTitle = ({
    service,
    targetName,
    targetAlias,
    subType,
  }: {
    service: string;
    subType?: string | null;
    targetName?: string | null;
    targetAlias?: string | null;
  }) => {
    const titlePrefix = CLOUD_ALL_SERVICE_LABEL?.[service] ?? service;
    const isDocumentDb = service === AWS_SERVICE.DOCUMENTDB && subType;
    return `${titlePrefix}${isDocumentDb ? ` ${startCase(subType)}` : ''}: ${targetName}${targetAlias ? ` (${targetAlias})` : ''}`;
  };

  const validateCloudDetail = async (
    rawParams: CloudDetailInput,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedData?: z.infer<typeof cloudDetailInputSchema>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type?: any;
  }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;

    let params: CloudDetailInput;
    try {
      params = await getDetailParams(rawParams);
    } catch (error) {
      if (checkIsPermissionError(error)) {
        // 406 인터셉터에서 이미 NO_PERMISSION 토스트를 표시하므로 별도 출력 안 함
        return { reason: 'permission_denied' };
      }
      throw error;
    }
    const parseResult = cloudDetailInputSchema.safeParse(params);

    // 대상 필수값 검증
    if (checkParams && !parseResult.success) {
      if (options.showErrorMessage) {
        const missingParams = parseResult.error.issues.map((issue) => issue.path[0]).join(', ');
        showErrorMsg({
          msgStr: t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', { value: missingParams }),
        });
      }
      return { reason: 'invalid_params' };
    }
    if (!parseResult.success) {
      return { reason: 'invalid_params' };
    }

    const data = parseResult.data;
    const { resourceId, cloud, service } = data;

    const permissionKey = unref(rolePermissionKey);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let type: any;

    // 권한 검증
    if (checkPermission) {
      if (permissionKey) {
        type = getDetailKeyFromPermissionKey(permissionKey as string, cloud, service);
        if (
          isDetailSlidePermissionDenied({
            rolePermissionKey: permissionKey,
            type,
            showError: options.showErrorMessage,
          })
        ) {
          return { reason: 'permission_denied' };
        }
      } else {
        let denied = true;

        const cloudServiceType = getCloudServiceType(service);
        if (cloudServiceType) {
          type = TOTAL_DETAIL[`CLOUD_${cloudServiceType}`];
          denied = isDetailSlidePermissionDenied({ type, showError: false });
        }

        // 중복 권한으로 한번 더 확인(multiCloud > ec2일지 aws > ec2일지 모르기 때문)
        if (denied && cloud === CLOUD.AWS) {
          type = TOTAL_DETAIL[`AWS_${service}`];
          denied = isDetailSlidePermissionDenied({ type, showError: false });
        }

        if (denied) {
          if (options.showErrorMessage) showErrorMsg({ msgStr: t('MESSAGE.NO_PERMISSION') });
          return { reason: 'permission_denied' };
        }
      }
    }

    // 대상 유효성 검증
    if (checkTarget) {
      const targetValidation = await checkIsValidTarget(
        {
          targetId: resourceId,
          targetKind: `${cloud.toUpperCase()}_${service}` as TargetStatusRequestTargetKind,
        },
        { showErrorMessage: options.showErrorMessage },
      );
      if (!targetValidation.valid) return { reason: targetValidation.reason };
    }

    // 디테일 타입 검증
    if (isNil(type) || EXCLUDE_DETAIL_CATEGORY.includes(`${cloud}_${service.toLowerCase()}`)) {
      if (options.showErrorMessage) showErrorMsg({ msgStr: t('MESSAGE.UNSUPPORTED_DETAIL_TYPE') });
      return { reason: 'invalid_detail_type' };
    }

    return { reason: 'ok', parsedData: data, type };
  };

  const openDetail = async ({
    params: rawParams,
    options,
    state,
    selectedTab,
    init,
    currentState,
  }: SlideDetailAdapterParams<CloudDetailInput>): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
    } = options ?? {};

    const { reason, parsedData, type } = await validateCloudDetail(rawParams, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedData) return createSlideDetailResult(reason);

    const data = parsedData;
    const {
      resourceId,
      cloud,
      service,
      subType,
      clusterMode,
      targetName,
      targetAlias,
      fromTime,
      toTime,
    } = data;

    // 디테일 패널 열기
    const result = addTabPanel(
      {
        title: getDetailTitle({ service, subType, targetName, targetAlias }),
        type: type!,
        name: targetName,
        addition: {
          cloud,
          service,
          resourceId,
          subType,
          clusterMode,
          fromTime,
          toTime,
        },
        state: { ...state, selectedTab },
        init,
      },
      currentState,
    );

    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return {
    isSlideDetailOpened,
    openDetail,
  };
};
